import { useNavigate } from "react-router-dom";
import { Download, Smartphone, Monitor, Apple, Chrome } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import useInstallPrompt from "@/hooks/useInstallPrompt";
import { toast } from "sonner";

const InstallPage = () => {
  const navigate = useNavigate();
  const { install, canInstall, isIos } = useInstallPrompt();

  const handleInstall = async () => {
    if (isIos) {
      toast.info("Su iOS: tocca Condividi → 'Aggiungi a schermata Home'", {
        duration: 6000,
      });
    } else if (canInstall) {
      try {
        await install();
        toast.success("App installata con successo!");
      } catch (error) {
        toast.error("Errore durante l'installazione");
      }
    } else {
      toast.info("L'app è già installata o il browser non supporta l'installazione");
    }
  };

  const isAndroid = /android/i.test(navigator.userAgent);
  const isDesktop = !isIos && !isAndroid;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 bg-primary rounded-lg flex items-center justify-center">
              <Smartphone className="text-primary-foreground" size={24} />
            </div>
            <h1 className="text-xl font-bold tracking-tight">
              <span className="text-primary">Tyre</span>Track
            </h1>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate("/login")}
            className="text-sm"
          >
            Accedi
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <div className="max-w-4xl mx-auto px-4 py-12 md:py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center h-20 w-20 bg-primary/10 rounded-2xl mb-6">
            <Download className="text-primary" size={40} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Installa <span className="text-primary">TyreTrack</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Gestisci il tuo magazzino pneumatici ovunque tu sia. Installa l'app sul tuo dispositivo per un accesso rapido e un'esperienza ottimale.
          </p>
        </div>

        {/* Install Button */}
        {canInstall && (
          <div className="flex justify-center mb-16">
            <Button
              size="lg"
              onClick={handleInstall}
              className="h-14 px-8 text-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/90 btn-glow shadow-lg"
            >
              <Download size={24} className="mr-3" />
              Installa App Ora
            </Button>
          </div>
        )}

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          <Card className="bg-card border-border">
            <CardContent className="p-6 text-center">
              <div className="h-12 w-12 bg-green-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Smartphone className="text-green-500" size={24} />
              </div>
              <h3 className="font-semibold mb-2">Accesso Rapido</h3>
              <p className="text-sm text-muted-foreground">
                Apri l'app direttamente dalla schermata home del tuo dispositivo
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6 text-center">
              <div className="h-12 w-12 bg-purple-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Download className="text-purple-500" size={24} />
              </div>
              <h3 className="font-semibold mb-2">Nessun Download</h3>
              <p className="text-sm text-muted-foreground">
                Nessun app store, installa direttamente dal browser
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Platform-specific Instructions */}
        <div className="space-y-8">
          <h2 className="text-2xl font-bold text-center mb-8">
            Come Installare
          </h2>

          {/* iOS Instructions */}
          {(isIos || isDesktop) && (
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Apple className="text-primary" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-3">iPhone / iPad</h3>
                    <ol className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex gap-2">
                        <span className="font-semibold text-primary">1.</span>
                        <span>Apri questa pagina in <strong>Safari</strong></span>
                      </li>
                      <li className="flex gap-2">
                        <span className="font-semibold text-primary">2.</span>
                        <span>Tocca il pulsante <strong>Condividi</strong> (icona quadrato con freccia) nella barra in basso</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="font-semibold text-primary">3.</span>
                        <span>Scorri e tocca <strong>"Aggiungi a schermata Home"</strong></span>
                      </li>
                      <li className="flex gap-2">
                        <span className="font-semibold text-primary">4.</span>
                        <span>Tocca <strong>"Aggiungi"</strong> in alto a destra</span>
                      </li>
                    </ol>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Android Instructions */}
          {(isAndroid || isDesktop) && (
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Chrome className="text-primary" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-3">Android</h3>
                    <ol className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex gap-2">
                        <span className="font-semibold text-primary">1.</span>
                        <span>Clicca il pulsante <strong>"Installa App Ora"</strong> in questa pagina</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="font-semibold text-primary">2.</span>
                        <span>Oppure tocca il menu (⋮) in alto a destra</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="font-semibold text-primary">3.</span>
                        <span>Seleziona <strong>"Installa app"</strong> o <strong>"Aggiungi a schermata Home"</strong></span>
                      </li>
                      <li className="flex gap-2">
                        <span className="font-semibold text-primary">4.</span>
                        <span>Conferma l'installazione</span>
                      </li>
                    </ol>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Desktop Instructions */}
          {isDesktop && (
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Monitor className="text-primary" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-3">Desktop (Chrome / Edge)</h3>
                    <ol className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex gap-2">
                        <span className="font-semibold text-primary">1.</span>
                        <span>Clicca il pulsante <strong>"Installa App Ora"</strong> in questa pagina</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="font-semibold text-primary">2.</span>
                        <span>Oppure clicca l'icona <strong>installa</strong> (⊕) nella barra degli indirizzi</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="font-semibold text-primary">3.</span>
                        <span>Clicca <strong>"Installa"</strong> nella finestra di conferma</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="font-semibold text-primary">4.</span>
                        <span>L'app si aprirà in una finestra dedicata</span>
                      </li>
                    </ol>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-card border border-border rounded-2xl p-8">
            <h3 className="text-xl font-semibold mb-3">Hai già installato l'app?</h3>
            <p className="text-muted-foreground mb-6">
              Accedi per iniziare a gestire il tuo magazzino pneumatici
            </p>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/login")}
              className="h-12 px-6"
            >
              Vai al Login
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border mt-20">
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} TyreTrack - Gestione Pneumatici
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Progressive Web App - Funziona su tutti i dispositivi
          </p>
        </div>
      </footer>
    </div>
  );
};

export default InstallPage;
